
// ABOUTME: Dashboard header component displaying lifetime and live referral statistics
// ABOUTME: Fetches and calculates metrics directly from Supabase

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useReferrals } from '@/hooks/useReferrals';

const DashboardHeader = () => {
  const { profile } = useAuth();
  const { referrals, isLoading } = useReferrals();

  // Calculate lifetime stats
  const totalReferrals = referrals?.length || 0;
  const interviewedReferrals = referrals?.filter(r => ['interview_scheduled', 'interviewed', 'hired'].includes(r.status)).length || 0;
  const hiredReferrals = referrals?.filter(r => r.status === 'hired').length || 0;
  
  const lifetimeStats = {
    totalReferrals,
    interviewSuccessRatio: totalReferrals > 0 ? Math.round((interviewedReferrals / totalReferrals) * 100) : 0,
    offerSuccessRatio: totalReferrals > 0 ? Math.round((hiredReferrals / totalReferrals) * 100) : 0,
    totalBonus: Math.round(profile?.total_earnings || 0)
  };

  // Calculate live stats
  const activeReferrals = referrals?.filter(r => ['submitted', 'under_review'].includes(r.status)).length || 0;
  const activeInterviews = referrals?.filter(r => ['interview_scheduled', 'interviewed'].includes(r.status)).length || 0;
  const activeOffers = referrals?.filter(r => r.status === 'hired').length || 0;
  const potentialBonus = referrals?.filter(r => !['rejected', 'withdrawn'].includes(r.status) && !r.bonus_paid)
    .reduce((sum, r) => sum + (r.bonus_amount || 0), 0) || 0;

  const liveStats = {
    activeReferrals,
    activeInterviews,
    activeOffers,
    potentialBonus: Math.round(potentialBonus)
  };

  if (isLoading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="h-32 bg-muted rounded-lg" />
        <div className="h-32 bg-muted rounded-lg" />
      </div>
    );
  }
  const StatCard = ({ label, value }: { label: string; value: string | number }) => (
    <Card className="bg-background border-border shadow-sm">
      <CardContent className="p-4 text-center">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
          {label}
        </div>
        <div className="text-2xl font-bold text-foreground">
          {value}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8">
      {/* Lifetime Referrals Section */}
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-4">Lifetime Referrals</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Referrals" value={lifetimeStats.totalReferrals} />
          <StatCard label="Interview Success Ratio" value={`${lifetimeStats.interviewSuccessRatio}%`} />
          <StatCard label="Offer Success Ratio" value={`${lifetimeStats.offerSuccessRatio}%`} />
          <StatCard label="Total Bonus" value={`$${lifetimeStats.totalBonus.toLocaleString()}`} />
        </div>
      </div>

      {/* Live Referrals Section */}
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-4">Live Referrals</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Active Referrals" value={liveStats.activeReferrals} />
          <StatCard label="Active Interviews" value={liveStats.activeInterviews} />
          <StatCard label="Active Offers" value={liveStats.activeOffers} />
          <StatCard label="Potential Bonus" value={`$${liveStats.potentialBonus.toLocaleString()}`} />
        </div>
      </div>

      {/* Separator */}
      <div className="border-t border-border" />
    </div>
  );
};

export default DashboardHeader;
